import React, { useCallback, useEffect, useState } from 'react';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Card, Spinner, Alert, Table, Btn, Input, Modal, Badge, Select } from '../../components/UI';
import { api, getUser } from '../../utils/api';

// Pipeline radna mesta (Faza 2): Recruitment (BP + Interviewer), CFA
// leadership (CFA Leader) i Ice Pilot Growth leadership (Growth Leader).
// Jedna cev, dva pogleda — isti podaci koje kandidat vidi sa svoje strane.

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}><Spinner /></div>;
}

function useCandidates() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const reload = useCallback(() => {
    api.cfa.getCandidates()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

const hexacoShort = { pending: '—', invited: 'invited', completed: 'done', debriefed: 'done · debriefed' };

// ── Detail modal sa istorijom ──────────────────────────────────────────────
function CandidateModal({ id, onClose, onChanged }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const load = useCallback(() => {
    api.cfa.getCandidate(id)
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function advance() {
    try {
      await api.cfa.advanceCandidate(id);
      load(); onChanged();
    } catch (e) { alert(e.message); }
  }

  const c = state.data?.candidate;
  return (
    <Modal open onClose={onClose} title={c ? c.name : 'Candidate'}>
      {state.loading ? <Loading /> : state.error ? <Alert type="error">{state.error}</Alert> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge status="active">{c.stageLabel}</Badge>
            <Badge status="default">HEXACO: {hexacoShort[c.hexaco] || c.hexaco}</Badge>
            {c.className && <Badge status="default">{c.className}</Badge>}
          </div>
          <div style={{ fontSize: '0.88rem', lineHeight: 1.9 }}>
            {c.country && <div>▪ Country: {c.country}</div>}
            <div>▪ Mentor: {c.mentor || '—'} · Interviewer: {c.interviewer || '—'}</div>
            <div>▪ Business Partner: {c.bp}</div>
            {c.stage === 'cfa' && <div>▪ Formation: {c.formationProgress}/{c.formationTotal}</div>}
          </div>
          {c.stage !== 'certified' && (
            <Btn size="sm" onClick={advance} style={{ alignSelf: 'flex-start' }}>Advance stage →</Btn>
          )}
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: '8px 0 6px' }}>
              History
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto', fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {state.data.history.map((h, i) => (
                <div key={i} style={{ borderLeft: '2px solid var(--canvas-warm)', paddingLeft: 10 }}>
                  <div>{h.Detail}</div>
                  <div style={{ color: 'var(--ink-faint)', fontSize: '0.72rem' }}>
                    {(h.CreatedBy || '').trim() || 'system'} · {new Date(h.CreatedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Recruitment (CFA) — BP + Interviewer ──────────────────────────────────
export function Recruitment() {
  const { loading, error, data, reload } = useCandidates();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [openId, setOpenId] = useState(null);
  const user = getUser();
  const isInterviewerOnly = (user?.funcs || []).includes('Interviewer') && !(user?.roles || []).includes('BP');

  async function initiate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.cfa.createCandidate({ fullName: name, country });
      setName(''); setCountry(''); reload();
    } catch (err) { alert(err.message); }
  }

  return (
    <BasecampLayout>
      <PageHeader title="Recruitment pipeline (CFA)" subtitle="Same data the candidate sees from their side — one pipeline, two views" />
      {isInterviewerOnly && <Alert type="info">You see candidates assigned to you as interviewer or mentor.</Alert>}
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <>
          <Card style={{ padding: '10px 18px', margin: '18px 0' }}>
            <Table
              headers={['Candidate', 'Stage', 'HEXACO', 'Formation', 'Interviewer', 'Mentor', '']}
              rows={data.candidates.map(c => [
                <b>{c.name}</b>,
                c.stageLabel,
                hexacoShort[c.hexaco] || c.hexaco,
                c.stage === 'cfa' || c.formationProgress ? `${c.formationProgress}/${c.formationTotal}` : '—',
                c.interviewer || '—',
                c.mentor || '—',
                <Btn size="sm" variant="outline" onClick={() => setOpenId(c.id)}>Open</Btn>,
              ])}
              emptyMessage="No candidates yet"
            />
          </Card>
          {!isInterviewerOnly && (
            <form onSubmit={initiate} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 560 }}>
              <Input placeholder="Candidate name" value={name} onChange={e => setName(e.target.value)} style={{ flex: 2, minWidth: 180 }} />
              <Input placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
              <Btn type="submit">Initiate candidate</Btn>
            </form>
          )}
          <p style={{ marginTop: 18, fontSize: '0.8rem', color: 'var(--ink-faint)' }}>
            Ongoing results update live, per the IP Agreement access rules. Process owner: Ice Pilot Growth Leader.
          </p>
        </>
      )}
      {openId != null && <CandidateModal id={openId} onClose={() => setOpenId(null)} onChanged={reload} />}
    </BasecampLayout>
  );
}

// ── CFA leadership — Class workplace ──────────────────────────────────────
export function CfaLead() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const load = useCallback(() => {
    api.cfa.getClasses()
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function markDone(sessionId) {
    try { await api.cfa.updateSession(sessionId, { status: 'done' }); load(); }
    catch (e) { alert(e.message); }
  }

  return (
    <BasecampLayout>
      <PageHeader title="CFA leadership" subtitle="Organising and running the six 2-hour online preparation sessions, watching the class's progress" />
      {state.loading ? <Loading /> : state.error ? <Alert type="error">{state.error}</Alert> : state.data.classes.map(cls => (
        <div key={cls.id} style={{ marginBottom: 32 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', marginBottom: 14 }}>
            {cls.name} {cls.leader ? <span style={{ fontSize: '0.85rem', color: 'var(--ink-faint)' }}>· leader: {cls.leader}</span> : null}
          </h2>
          <div className="grid-2col" style={{ gap: 24 }}>
            <Card style={{ padding: '18px 22px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>Preparation sessions — 6 × 2h, online</h3>
              <Table
                headers={['#', 'Session', 'Status / date', '']}
                rows={cls.sessions.map(s => [
                  s.SessionNo, s.Title, s.WhenText || s.Status,
                  s.Status === 'done' ? '✓' : <Btn size="sm" variant="outline" onClick={() => markDone(s.SessionID)}>Mark done</Btn>,
                ])}
              />
            </Card>
            <Card style={{ padding: '18px 22px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', marginBottom: 10 }}>Class progress</h3>
              <Table
                headers={['Candidate', 'Formation progress']}
                rows={cls.candidates.map(m => [
                  `${m.FullName}${m.Country ? ` (${m.Country})` : ''}`,
                  `${m.FormationProgress} / ${m.FormationTotal}`,
                ])}
                emptyMessage="No candidates in class"
              />
            </Card>
          </div>
        </div>
      ))}
    </BasecampLayout>
  );
}

// ── Ice Pilot Growth leadership — funnel ──────────────────────────────────
export function GrowthLead() {
  const [funnel, setFunnel] = useState({ loading: true, error: null, data: null });
  const { loading, error, data, reload } = useCandidates();
  const [openId, setOpenId] = useState(null);
  const [assign, setAssign] = useState({});
  const [members, setMembers] = useState([]);

  const loadFunnel = useCallback(() => {
    api.cfa.getFunnel()
      .then(d => setFunnel({ loading: false, error: null, data: d }))
      .catch(e => setFunnel({ loading: false, error: e.message, data: null }));
  }, []);
  useEffect(() => { loadFunnel(); api.getMembers().then(d => setMembers(d.members)).catch(() => {}); }, [loadFunnel]);

  async function appoint(candidateId) {
    const interviewerId = assign[candidateId];
    if (!interviewerId) return;
    try {
      await api.cfa.updateCandidate(candidateId, { interviewerUserID: parseInt(interviewerId, 10) });
      reload();
    } catch (e) { alert(e.message); }
  }

  const interviewers = members.filter(m => m.funcs.includes('Interviewer') || m.roles.includes('IP'));

  return (
    <BasecampLayout>
      <PageHeader title="Ice Pilot Growth leadership" subtitle="Selection → formation → qualification → certification, led end to end. Ongoing results visible at all times to Shareholders and related Business Partners — transparency built in, not reported." />
      {funnel.loading ? <Loading /> : funnel.error ? <Alert type="error">{funnel.error}</Alert> : (
        <Card style={{ padding: '18px 22px', marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>The funnel — live</h3>
          <Table
            headers={['Stage', 'Now', 'Note']}
            rows={funnel.data.funnel.map(f => [
              f.label, f.count,
              f.key === 'cfa' && funnel.data.avgFormationProgress != null
                ? `avg formation progress ${funnel.data.avgFormationProgress.toFixed(1)}/10` : '—',
            ])}
          />
        </Card>
      )}
      {loading ? <Loading /> : error ? <Alert type="error">{error}</Alert> : (
        <Card style={{ padding: '18px 22px' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', marginBottom: 10 }}>Appoint interviewers · all candidates</h3>
          <Table
            headers={['Candidate', 'Stage', 'Interviewer', 'Appoint', '']}
            rows={data.candidates.map(c => [
              <b>{c.name}{c.country ? ` (${c.country})` : ''}</b>,
              c.stageLabel,
              c.interviewer || '—',
              <span style={{ display: 'flex', gap: 6 }}>
                <Select value={assign[c.id] || ''} onChange={e => setAssign(a => ({ ...a, [c.id]: e.target.value }))} style={{ maxWidth: 150, padding: '5px 8px' }}>
                  <option value="">—</option>
                  {interviewers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </Select>
                <Btn size="sm" variant="outline" onClick={() => appoint(c.id)}>Appoint</Btn>
              </span>,
              <Btn size="sm" variant="outline" onClick={() => setOpenId(c.id)}>Open</Btn>,
            ])}
            emptyMessage="No candidates"
          />
        </Card>
      )}
      {openId != null && <CandidateModal id={openId} onClose={() => setOpenId(null)} onChanged={() => { reload(); loadFunnel(); }} />}
    </BasecampLayout>
  );
}
